/**
 * Leesbare namen voor velden in het beheer. De teksteditor en instellingen worden
 * automatisch opgebouwd uit de datastructuur; hier staat hoe elk veld heet.
 */

export type FieldMeta = {
  label: string
  hint?: string
  /** Meerdere regels tekst. */
  long?: boolean
  /** Niet tonen (technisch veld). */
  hidden?: boolean
}

export const fieldLabels: Record<string, FieldMeta> = {
  // Algemeen
  eyebrow: { label: 'Regeltje boven de kop', hint: 'Klein, in kleur. Leeg laten mag.' },
  heading: { label: 'Kop' },
  text: { label: 'Tekst' },
  accent: { label: 'Cursief deel', hint: 'Komt schuin en in kleur achter de kop.' },
  body: { label: 'Tekst', long: true },
  cta: { label: 'Knoptekst' },
  secondaryCta: { label: 'Tweede knop' },
  note: { label: 'Kleine noot', long: true },
  title: { label: 'Titel' },
  description: { label: 'Omschrijving', long: true },
  intro: { label: 'Inleiding', long: true },
  claim: { label: 'Belofte / kernzin' },
  paragraphs: { label: 'Alinea’s', long: true },
  steps: { label: 'Stappen' },
  lines: { label: 'Kopregels', hint: 'Elke regel schuift apart in beeld.' },
  method: { label: 'Feitje naast ervaring' },
  label: { label: 'Label' },
  value: { label: 'Waarde' },
  topics: { label: 'Onderwerpen' },
  url: { label: 'Link' },
  episodes: { label: 'Afleveringen' },
  number: { label: 'Nummer' },
  guest: { label: 'Gast' },
  duration: { label: 'Duur' },
  summary: { label: 'Samenvatting', long: true },
  includes: { label: 'Bij elke behandeling inbegrepen' },
  included: { label: 'Inbegrepen' },
  giftTiers: { label: 'Treden' },
  codes: { label: 'Codes' },
  creators: { label: 'Klanten' },
  forWho: { label: 'Voor wie' },
  effects: { label: 'Wat het doet' },
  // "rhythm" komt voor bij behandelingen én in de merkstijl; één label past bij beide.
  rhythm: { label: 'Ritme' },
  quote: { label: 'Citaat', long: true },
  name: { label: 'Naam' },
  context: { label: 'Toelichting', hint: 'Bijv. “hormonale huid, 34”.' },
  perks: { label: 'Voordelen' },
  price: { label: 'Prijsregel' },
  smallPrint: { label: 'Kleine lettertjes' },
  byline: { label: 'Regel bij de foto' },
  articlesHeading: { label: 'Kop boven artikelen' },
  emptyCategory: { label: 'Tekst bij lege categorie' },
  compareHeading: { label: 'Kop boven de vergelijkingstabel' },
  faqTitle: { label: 'Kop boven de veelgestelde vragen' },
  anchorEyebrow: { label: 'Regeltje boven het uitgelichte protocol' },
  askLine: { label: 'Vraagregel onderaan' },
  contactNote: { label: 'Contactnoot', long: true },
  treatedBy: { label: 'Regel bij de foto' },
  includesTitle: { label: 'Kop boven “inbegrepen”' },
  agenda: { label: 'Agendablok' },
  salonDoor: { label: 'Deur naar de salon', hint: 'Linkt naar Behandelingen. Lege titel = niet tonen.' },
  shopDoor: { label: 'Deur naar de webshop', hint: 'Tokens: {count} = aantal producten, {freeShipping} = de verzendregel. Lege titel = niet tonen.' },
  homeShare: { label: 'Procent resultaat thuis', hint: 'De rest (100 min dit getal) toont de site als “in de salon”.' },
  salonLabel: { label: 'Onderschrift bij het salondeel' },
  homeLabel: { label: 'Onderschrift bij het thuisdeel' },
  channelsTitle: { label: 'Kop boven de soorten vragen' },
  channels: { label: 'Soorten vragen' },
  skin: { label: 'Vraag over huid of product (WhatsApp)' },
  booking: { label: 'Afspraak in de salon (afsprakenlade)' },
  advice: { label: 'Uitgebreid advies (online consult)' },
  order: { label: 'Bestelling of retour (mail)' },
  late: { label: 'Te laat of verhinderd (WhatsApp)' },
  visit: { label: 'Blok “kom langs”', hint: 'Adres en dagen komen uit Instellingen › Salon & contact en › Salondagen.' },
  arriveTitle: { label: 'Kop boven “zo kom je er”' },
  mapCta: { label: 'Knop om de kaart te laden' },
  mapNote: { label: 'Uitleg bij de kaart (privacy)', long: true },

  // Paginablokken
  hero: { label: 'Bovenste deel van de pagina' },
  media: { label: 'Beeld', hint: 'Een foto of korte, stille video. Wat je hier kiest staat groot op de site.' },
  concerns: { label: 'Blok “waar loopt jouw huid tegenaan?”', hint: 'De klachten zelf beheer je bij Aanbod › Huidklachten.' },
  story: { label: 'Blok “salon & werkwijze” (op klei)', hint: 'Salon, werkwijze en online consult in één verhaal.' },
  consultLine: { label: 'Regel naar het online consult', hint: 'Opent de afsprakenlade bij het consult. Leeg = niet tonen.' },
  bestsellers: { label: 'Blok “bestsellers”' },
  reviews: { label: 'Blok “wat cliënten zeggen”' },
  protocols: { label: 'Blok “protocollen”' },
  journal: { label: 'Blok “journal”' },
  gifts: { label: 'Blok “wat erbij komt”' },
  others: { label: 'Overige protocollen' },
  salonClient: { label: 'Blok “al klant in de salon?”' },
  bindweefsel: { label: 'Uitleg bindweefselmassage (op klei)' },
  routines: { label: 'Blok “liever niet zelf puzzelen?”', hint: 'Toont de eerste protocollen uit Aanbod › Protocollen.' },
  bridge: { label: 'Blok “van salon naar thuis”' },
  closing: { label: 'Slotblok' },
  podcast: { label: 'De podcast' },
  philosophy: { label: 'Filosofie (op klei)' },
  manifest: { label: 'Manifest (de grote zin die zich vult bij scrollen)', hint: 'Lege zin = het blok staat niet op de site.' },
  statement: { label: 'De zin', long: true, hint: 'Kort en in je eigen woorden; twee à drie zinnen werkt het best.' },
  ingredients: { label: 'Blok “wat er écht in zit”', hint: 'Een stof zonder product in het assortiment valt vanzelf weg.' },
  items: { label: 'Werkzame stoffen' },
  match: { label: 'Herkennen aan', hint: 'Stukjes tekst uit “werkzame stoffen” bij een product, bijv. “retin” of “hyaluron”.' },
  effect: { label: 'Wat het doet', long: true },
  shelf: { label: 'Op mijn badkamerplank' },
  salon: { label: 'De salon' },

  // Instellingen: Sarah & salon
  firstName: { label: 'Voornaam' },
  role: { label: 'Rol', hint: 'Bijv. “Huidspecialist & oprichter”.' },
  portrait: { label: 'Portret (rond)' },
  atWork: { label: 'Foto aan het werk' },
  withProducts: { label: 'Foto met producten' },
  portraitPalm: { label: 'Portret (groot)' },
  city: { label: 'Stad of wijk' },
  street: { label: 'Straat en nummer' },
  postalCode: { label: 'Postcode' },
  locality: { label: 'Plaats' },
  address: { label: 'Volledig adres (één regel)' },
  routeUrl: { label: 'Link “plan je route”' },
  email: { label: 'E-mail' },
  emailAlt: { label: 'Tweede e-mail' },
  phone: { label: 'Telefoon (zoals getoond)' },
  phoneIntl: { label: 'Telefoon (internationaal)', hint: 'Bijv. +31636111580 — voor bellen vanaf mobiel.' },
  whatsapp: { label: 'WhatsApp-link' },
  instagram: { label: 'Instagram-link' },
  kvk: { label: 'KvK-nummer' },
  vatId: { label: 'Btw-id', hint: 'Bijv. NL001234567B01. Verplicht op de site en op facturen zodra je btw-plichtig bent.' },
  iban: { label: 'IBAN', hint: 'Op facturen, kassabonnen en creditnota’s. Leeg = niet getoond.' },
  invoicePrefix: { label: 'Voorvoegsel factuurnummer', hint: 'Bijv. “BB-” → factuur BB-1001. Bestelnummers in het beheer blijven gelijk. Leeg = alleen het nummer.' },
  englishNote: { label: 'Regel voor Engelstaligen' },
  transit: { label: 'Met het openbaar vervoer', hint: 'Op de contactpagina. Leeg = niet getoond.', long: true },
  parking: { label: 'Parkeren', hint: 'Leeg = niet getoond.', long: true },
  entrance: { label: 'Aanbellen en binnenkomen', hint: 'Bijv. welke bel, welke verdieping.', long: true },
  accessibility: { label: 'Toegankelijkheid', hint: 'Trap, drempel, lift. Alleen invullen wat zeker klopt.', long: true },

  // Instellingen: afspraken
  freeShippingFrom: { label: 'Gratis verzending vanaf (€)', hint: 'Dit is ook de eerste trede van de cadeaustaffel.' },
  shippingCost: { label: 'Verzendkosten daaronder (€)' },
  returnDays: { label: 'Ruiltermijn op huidadvies (dagen)' },
  orderCutoff: { label: 'Besteld vóór (tijd)', hint: 'Voor dit tijdstip besteld = morgen in huis.' },
  yearsExperience: { label: 'Jaren ervaring' },
  responseHours: { label: 'Reactietijd (uren)' },
  giftConsultMinutes: { label: 'Duur videoconsult in cadeau (min)' },
  cancelHours: { label: 'Zelf verzetten tot (uren vooraf)', hint: 'Daarna kan een klant alleen nog via WhatsApp verzetten.' },
  refillWarnDays: { label: '“Bijna op” vanaf (dagen)', hint: 'Zo lang vóór een product op is, krijgt de klant een seintje in Mijn omgeving.' },
  courseSpacingWeeks: { label: 'Weken tussen kuursessies' },
  courseMaxSessions: { label: 'Maximaal aantal sessies per kuur' },
  kuurValidMonths: { label: 'Vooruitbetaalde kuur geldig (maanden)' },
  kuurTransferable: { label: 'Sessie mag op een andere behandeling' },
  noShowUsesSession: { label: 'Niet gekomen telt als gebruikte kuursessie' },
  revisitAfterWeeks: { label: 'Uitnodiging na (weken zonder bezoek)', hint: 'Daarna ziet een salonklant in Mijn omgeving een zachte uitnodiging.' },
  diaryEveryDays: { label: 'Fotodagboek-herinnering na (dagen)' },
  vatRate: { label: 'Btw op facturen (%)' },
  followUpWeeks: { label: 'Appje na bestelling (weken)', hint: 'Staat op de bedankpagina: wanneer je vraagt hoe het gaat.' },
  giftCardValidMonths: { label: 'Cadeaubon geldig (maanden)' },
  giftCardValidFor: { label: 'Nieuwe cadeaubon geldt voor' },
  magicLinkMinutes: { label: 'Inloglink werkt (minuten)', hint: 'Moet gelijk zijn aan de instelling op de server.' },
  subscription: { label: 'Bewust Abonnement' },
  giftEvery: { label: 'Cadeau bij elke … levering', hint: 'Bijv. 3 = elke derde levering.' },
  videoChecksPerYear: { label: 'Huidchecks per jaar via video' },
  leadDays: { label: 'Eerste levering na starten (dagen)', hint: 'Ook na hervatten van een pauze.' },
  changeUntilDays: { label: 'Aanpassen kan tot (dagen voor levering)', hint: 'Staat ook in de algemene voorwaarden.' },
  defaultIntervalWeeks: { label: 'Standaardritme nieuw abonnement (weken)', hint: 'De klant kan dit zelf aanpassen.' },
  consult: { label: 'Online consult (Shelfie Time)' },
  slug: { label: 'Code', hidden: true },
  minutes: { label: 'Duur (min)' },
  creditFrom: { label: 'Verrekend vanaf bestelling van (€)' },
  creditDays: { label: 'Verrekenen binnen (dagen na consult)' },

  // Cadeaustaffel & codes
  threshold: { label: 'Vanaf bedrag (€)' },
  phrase: { label: 'Zo klinkt het in een zin', hint: 'Voorbeeld: “Nog € 38 tot een reisformaat cadeau”.' },
  ruleId: { label: 'Code van de cadeauregel', hidden: true },
  stockItemId: { label: 'Voorraadartikel', hidden: true },
  choiceStockItems: { label: 'Voorraadartikel per keuze', hidden: true },
  choices: { label: 'Waaruit de klant kiest', hint: 'Bijv. “Reisformaat cleanser” en “Reisformaat toner”. Leeg = geen keuze.' },
  code: { label: 'Code', hint: 'Hoofdletters, zonder spaties.' },
  gift: { label: 'Wat de klant ermee krijgt' },
  handle: { label: 'Instagram-naam' },
  image: { label: 'Foto' },
  pickId: { label: 'Aanbevolen product' },
  logos: { label: 'Logo’s' },
  mentions: { label: 'Ook genoemd in' },
  size: { label: 'Formaat logo' },

  // Marketing: merkstijl
  taglines: { label: 'Slogans', hint: 'Korte zinnen die het merk dragen. Gebruik ze op posts, flyers en in je bio.' },
  messages: { label: 'Kernboodschappen' },
  toneDo: { label: 'Wel doen' },
  toneDont: { label: 'Niet doen' },
  hashtags: { label: 'Hashtags' },
  moment: { label: 'Wanneer' },
  what: { label: 'Wat' },

  // Mijn omgeving
  tips: { label: 'Tip van de week', hint: 'De site kiest zelf welke tip past bij haar routine of salonbezoek.' },
  retinoidWeek: { label: 'Eerste weken retinal', hint: 'Gebruik {week} en {product}.' },
  firstCycle: { label: 'Na de eerste huidcyclus (vitamine C)', hint: 'Gebruik {weeks} en {product}.' },
  firstWeekMany: { label: 'Eerste week, meerdere nieuwe producten' },
  firstWeekOne: { label: 'Eerste week, één nieuw product', hint: 'Gebruik {product}.' },
  patience: { label: 'Routine loopt al een tijdje' },
  afterTreatment: { label: 'Vlak na een behandeling', hint: 'Gebruik {treatment}.' },
  whereToStart: { label: 'Nog niets afgenomen' },
  betweenSessions: { label: 'Tussen twee kuursessies', hint: 'Gebruik {courseSpacing}.' },
  keepResult: { label: 'Na de salon, zonder thuisroutine' },
  seasons: { label: 'Seizoensmeldingen' },
  cold: { label: 'Najaar en winter' },
  spring: { label: 'Lente' },
  summerRetinal: { label: 'Zomer, met retinal', hint: 'Gebruik {product}.' },
  withProduct: { label: 'Als ze het product gebruikt', hint: 'Gebruik {product}.', long: true },
  stopReasons: { label: 'Redenen bij stoppen', hint: 'De lijst ligt vast, zodat Inzicht de redenen kan tellen. Je past alleen de tekst aan.' },
  genoeg: { label: 'Nog genoeg (stelt pauzeren voor)' },
  'te-duur': { label: 'Te duur' },
  huid: { label: 'Huid reageert niet goed (stelt een bericht aan jou voor)' },
  'iets-anders': { label: 'Probeert iets anders' },
  anders: { label: 'Andere reden (met ruimte voor een toelichting)' },
  suggest: { label: 'Wat we dan voorstellen' },
  intervals: { label: 'Ritmes om uit te kiezen (weken)' },
  pauseOptions: { label: 'Pauzekeuzes' },
  weeks: { label: 'Weken' },
}

/** Velden die een afbeelding zijn — krijgen een beeldkiezer. */
export const imageKeys = new Set(['image', 'portrait', 'atWork', 'withProducts', 'portraitPalm'])

/** Keuzevelden met vaste opties. */
export const selectOptions: Record<string, { value: string; label: string }[]> = {
  size: [
    { value: 'sm', label: 'Breed logo (laag)' },
    { value: 'lg', label: 'Hoog logo' },
  ],
  suggest: [
    { value: 'pauze', label: 'Pauzeren voorstellen' },
    { value: 'sarah', label: 'Eerst een berichtje aan jou' },
    { value: 'geen', label: 'Niets, gewoon stoppen' },
  ],
}

/** De naam van een veld: uit de lijst hierboven, anders "firstCycle" → "First cycle". */
export const humanize = (key: string) => fieldLabels[key]?.label ?? key.replace(/([A-Z])/g, ' $1').replace(/^./, (c) => c.toUpperCase())

/** Tokens die in teksten gebruikt kunnen worden — met uitleg voor Sarah. */
export const tokenHelp: { token: string; meaning: string }[] = [
  { token: '{firstName}', meaning: 'je voornaam' },
  { token: '{name}', meaning: 'je volledige naam' },
  { token: '{city}', meaning: 'stad of wijk van de salon' },
  { token: '{years}', meaning: 'jaren ervaring' },
  { token: '{phone}', meaning: 'je telefoonnummer' },
  { token: '{responseHours}', meaning: 'reactietijd in uren' },
  { token: '{consultName}', meaning: 'naam van het online consult' },
  { token: '{consultMinutes}', meaning: 'duur van het consult' },
  { token: '{consultPrice}', meaning: 'prijs van het consult' },
  { token: '{cancelHours}', meaning: 'uren vooraf kosteloos verzetten' },
]
